import { SimulationRender } from './../interfaces/simulation-render';
import { Injectable } from '@angular/core';
import { CanvasService } from './canvas.service';
import { grpc } from '@improbable-eng/grpc-web';
import * as THREE from 'three';
import { HttpClient } from '@angular/common/http';
import { GrpcSimulationService } from '../generated/simulation_pb_service';
import { SimulationStep } from '../generated/models_pb';
import { RunRequest, RunResponse } from '../generated/simulation_pb';
import { PaintCell } from '../generated/paint_pb';
import { Store } from '@ngxs/store';
import { NotificationsActions } from 'src/app/state/notifications/notifications.actions';
import { Severity } from 'src/app/global/enums/severity';
import {
  RenderState,
  SimulationDataState,
  SimulationState,
} from '../enums/simulation-state';
import { SimulationRendererService } from './simulation-renderer.service';
import { DelayService } from './delay.service';
import { IndexPointService } from './index-point.service';
import { RequestHeaderService } from './request-header.service';
import { InactivityService } from './inactivity.service';
import { BehaviorSubject, catchError, Observable, of } from 'rxjs';
import { calculateComplexity } from './simulation-details-utility';
import { ApiTerrainService } from './api-terrain.service';
import { CellColoring } from '../interfaces/simulation-results-cell-coloring';
import { PreSimulationDetails } from '../models/pre-simulation-details';

@Injectable({
  providedIn: 'root',
})
export class SimulationService {
  constructor(
    private canvasService: CanvasService,
    private delayService: DelayService,
    private requestHeaderService: RequestHeaderService,
    private http: HttpClient,
    private apiTerrainService: ApiTerrainService,
    private rendererService: SimulationRendererService,
    private store: Store,
    private indexPointService: IndexPointService,
    private inactivityService: InactivityService
  ) {
    this.rendererService.onCompleted = () => {
      if (this.allStepsAreReceivedAndRendered()) {
        this.changeSimulationState(SimulationState.Completed);
        this.store.dispatch(
          new NotificationsActions.Toast(
            'Simulation completed.',
            Severity.Success
          )
        );
        if (this.onRunCompleted) {
          this.onRunCompleted();
        }
      }
    };
    this.rendererService.onFailed = (error: Error) => {
      this.changeSimulationState(SimulationState.Failed);
      const toastMessage: string = 'Simulation failed during rendering.';
      console.error(toastMessage, error);
      this.store.dispatch(
        new NotificationsActions.Toast(toastMessage, Severity.Error)
      );
    };
  }

  private simulationStartdOn?: number;

  /** callback function to be called when the simulation is completed */
  public onRunCompleted?: () => void;

  /** current state of the displayed simulation */
  private _simulationState: SimulationState = SimulationState.Ready;
  public isFinishclicked: boolean = false;

  public get simulationState(): SimulationState {
    return this._simulationState;
  }

  private simulationStateSubject = new BehaviorSubject<SimulationState>(
    this._simulationState
  );

  public get simulationState$(): Observable<SimulationState> {
    return this.simulationStateSubject.asObservable();
  }

  /** current state of the simualtions data */
  public simulationDataState: SimulationDataState = SimulationDataState.None;

  public changeSimulationState(state: SimulationState) {
    if (state !== this.simulationState) {
      this._simulationState = state;
      this.simulationStateSubject.next(this._simulationState);
      console.debug('Simulation state changed to: ', state);
    }
  }

  private allSteps: SimulationRender[] = [];
  private instantRenderRequested: boolean = false;

  private changeSimulationDataState(state: SimulationDataState) {
    if (state !== this.simulationDataState) {
      this.simulationDataState = state;
      console.debug('Simulation data state changed to: ', state);
    }
  }

  private allStepsAreReceivedAndRendered(): boolean {
    if (this.simulationDataState !== SimulationDataState.Received) {
      return false;
    }
    for (const step of this.allSteps) {
      if (step.state !== RenderState.Rendered) {
        return false;
      }
    }
    return true;
  }

  /** pause the simulation */
  public pause(): void {
    this.changeSimulationState(SimulationState.Paused);
    this.rendererService.pause();
    for (const step of this.allSteps) {
      if (step.state === RenderState.Rendering) {
        step.state = RenderState.None;
      }
    }
  }

  /** resume the simulation steps */
  public resume(): void {
    this.changeSimulationState(SimulationState.Running);
    this.rendererService.resume(this.allSteps);
  }

  private addRange<T>(target: T[], source: T[]) {
    for (const item of source) {
      target.push(item);
    }
  }

  private async grpcRunOnmessage(message: RunResponse) {
    if (!this.simulationStartdOn) {
      this.simulationStartdOn = Date.now();
    }
    this.inactivityService.resetInactivityTimer();
    const stepList: SimulationStep.AsObject[] = message.toObject().stepsList;
    const complexityRate: number =
      message.toObject().totalspawned === 0
        ? 0
        : calculateComplexity(
          message.toObject().startingagents,
          message.toObject().totalspawned,
          message.toObject().totalunique
        );

    let totalCells: number[] = stepList.map((step) => step.index);
    if (this.allSteps.length > 0) {
      totalCells = this._unionSteps(
        totalCells,
        this.allSteps[this.allSteps.length - 1].totalCells
      );
    }
    this.allSteps.push({
      state: RenderState.None,
      steps: [],
      index: this.allSteps.length,
      startingAgents: message.toObject().startingagents,
      complexityRate,
      totalCells,
    });
    this.addRange(this.allSteps[this.allSteps.length - 1].steps, stepList);
    if (this.simulationState === SimulationState.Running) {
      await this.rendererService.render(this.allSteps);
    }
  }

  private _unionSteps(cells1: number[], cells2: number[]): number[] {
    return [...new Set([...cells1, ...cells2])];
  }

  private grpcRunOnEnd(
    code: grpc.Code,
    message: string,
    trailers: grpc.Metadata
  ) {
    if (code === grpc.Code.OK) {
      this.changeSimulationDataState(SimulationDataState.Received);
      if (this.rendererService.runDetails) {
        this.rendererService.runDetails.simulationEndedOn = Date.now();
      }
      console.log('The simulation data stream has ended.');
      if (this.instantRenderRequested) {
        this.rendererService.renderInstantly(this.allSteps);
        this.instantRenderRequested = false;
      }
      return;
    }
    this.changeSimulationDataState(SimulationDataState.Failed);
    this.changeSimulationState(SimulationState.Failed);
    console.error('gRPC Simulate: An error occured', code, message, trailers);
    for (const key in this) {
      console.log(key, this[key]);
    }
    const toastMessage: string = 'Simulation failed during execution.';
    this.store.dispatch(
      new NotificationsActions.Toast(toastMessage, Severity.Error)
    );
  }

  private async getSimulationSteps(simulationId: string) {
    const request: RunRequest = new RunRequest();
    request.setId(simulationId);
    this.changeSimulationDataState(SimulationDataState.Receiving);
    this.changeSimulationState(SimulationState.Running);

    this.requestHeaderService.getHeaderPromise().then((metadata) => {
      this.inactivityService.resetInactivityTimer();
      grpc.invoke(GrpcSimulationService.Run, {
        request: request,
        host: '',
        metadata: new grpc.Metadata(metadata),
        onMessage: this.grpcRunOnmessage.bind(this),
        onEnd: this.grpcRunOnEnd.bind(this),
      });
    });
  }

  private async getSimulationResults(simulationId: string) {
    const request: RunRequest = new RunRequest();
    request.setId(simulationId);
    this.changeSimulationDataState(SimulationDataState.Receiving);
    this.changeSimulationState(SimulationState.Running);

    this.requestHeaderService.getHeaderPromise().then((metadata) => {
      this.inactivityService.resetInactivityTimer();
      grpc.invoke(GrpcSimulationService.GetSimulationResult, {
        request: request,
        host: '',
        metadata: new grpc.Metadata(metadata),
        onMessage: this.grpcRunOnmessage.bind(this),
        onEnd: this.grpcRunOnEnd.bind(this),
      });
    });
  }

  public async runGetSimulationResult(simulationId: string) {
    this.resetAllSteps();
    await this.getSimulationResults(simulationId);
  }

  public runCellColoringResult(
    cellColorings: CellColoring[],
    clearCanvas: boolean = true
  ) {
    this.resetAllSteps();
    this.changeSimulationDataState(SimulationDataState.Receiving);
    this.changeSimulationState(SimulationState.Running);

    if (clearCanvas) {
      this.canvasService.eraseAllAgentsFromCanvas();
    }

    this.rendererService.paintCellColorings(cellColorings);

    this.changeSimulationState(SimulationState.Completed);
    this.store.dispatch(
      new NotificationsActions.Toast('Simulation completed.', Severity.Success)
    );
  }

  public async run(simulationId: string) {
    this.resetAllSteps();

    this.apiTerrainService
      .getPreSimulationDetails(simulationId)
      .pipe(
        catchError((err) => {
          console.error('Failed to get pre-simulation-details:', err);
          this.store.dispatch(
            new NotificationsActions.Toast(
              'Cannot get simulation details.',
              Severity.Error
            )
          );
          return of(undefined);
        })
      )
      .subscribe((details: PreSimulationDetails | undefined) => {
        if (details) {
          this.rendererService.runDetails = {
            complexityRating: 0,
            simulationStartedOn: Date.now(),
            simulationEndedOn: undefined,
            landslideCount: details.landslideCount,
            agentCount: details.agentCount,
            initiationPointsCount: details.initiationPointsCount,
          };
        }
      });
    await this.getSimulationSteps(simulationId);
  }

  public async nextStep(simulationId: string) {
    if (this.simulationState !== SimulationState.Ready) {
      this.rendererService.renderNextStep(this.allSteps);
      return;
    }
    this.run(simulationId);
    this.pause();
    setTimeout(() => {
      this.rendererService.renderNextStep(this.allSteps);
    }, 500);
  }

  public finish(simulationId: string) {
    console.log("isFinishClicked before: " + this.isFinishclicked)
    this.isFinishclicked = true;
    console.log("isFinishClicked after: " + this.isFinishclicked)

    if (this.simulationState !== SimulationState.Ready) {
      this.pause();
      if (this.simulationDataState === SimulationDataState.Received) {
        this.rendererService.renderInstantly(this.allSteps);
      } else {
        setTimeout(() => {
          if (this.simulationState === SimulationState.Completed) {
            return;
          }
          this.finish(simulationId);
        }, 500);
      }
      return;
    }
    this.instantRenderRequested = true;
    this.pause();
    this.run(simulationId);
  }

  public isLastStep(): boolean {
    if (
      this.simulationDataState !== SimulationDataState.Received ||
      this.simulationState !== SimulationState.Paused
    ) {
      return false;
    }
    const lastStepIndex: number = this.allSteps.length - 1;
    for (let i: number = 0; i < this.allSteps.length - 1; i++) {
      if (this.allSteps[i].state !== RenderState.Rendered) {
        return false;
      }
    }
    return this.allSteps[lastStepIndex].state === RenderState.None;
  }

  public canBeRun(): boolean {
    return (
      this.simulationState === SimulationState.Ready ||
      this.simulationState === SimulationState.Paused
    );
  }

  public async reset(simulationId?: string): Promise<void> {
    if (this.simulationState !== SimulationState.Ready) this.pause();
    this.rendererService.runDetails = undefined;
    this.simulationStartdOn = undefined;
    await this.delayService.delay(550);
    this.changeSimulationState(SimulationState.Ready);
    this.changeSimulationDataState(SimulationDataState.None);
    this.resetAllSteps();
    this.rendererService.reset();
    if (!simulationId) {
      return;
    }
    this.apiTerrainService.reset(simulationId).subscribe({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      next: (message: any) => {
        (message.paintCells.$values as PaintCell.AsObject[]).forEach(
          (element) => {
            if (!element.location) {
              throw Error('location is missing');
            }
            if (!element.color) {
              throw Error('color is missing');
            }
            const cellIndex: number = this.indexPointService.convertToIndex(
              element.location,
              this.canvasService.terrainGrid.cols
            );
            this.canvasService.paintedCells.push(cellIndex);
            this.canvasService.terrainGrid.cellsList[cellIndex].color =
              element.color;

            const color: THREE.Color = new THREE.Color(
              element.color.r,
              element.color.g,
              element.color.b
            );
            this.canvasService.paintCanvas(cellIndex, color);
            this.canvasService.addAgent(cellIndex);
          }
        );
        this.canvasService.updateTexture();
        this.canvasService.animate();
        this.store.dispatch(
          new NotificationsActions.Toast(
            'Simulation data is reset successfully.',
            Severity.Success
          )
        );
      },
      error: (err) => {
        console.error('Can not reset simulation', err);
        this.store.dispatch(
          new NotificationsActions.Toast(
            'Simulation reset failed.',
            Severity.Error
          )
        );
      },
    });
  }

  private resetAllSteps() {
    this.allSteps = [
      {
        state: RenderState.None,
        steps: [],
        index: 0,
        startingAgents: 0,
        complexityRate: 0,
        totalCells: [],
      },
    ];
  }

  public getSelectedScenarios(id: string) {
    return this.http.get(
      `/api/simulationTracker/scenarios/${id}`
    );
  }
}
